import { Checkbox, message, Spin, Tooltip } from "antd";
import { useRequest } from "ahooks";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { PopoverText } from "@/components";
import useDragDrop, { useDragDropDataTransformer } from "@/hooks/useDragDrop";
import {
    PickListExportFieldsStatus,
    PickListExportItem,
    savePickListExportFieldsStatus,
} from "@/services/api/moreFeatures/pickList";
import { orderRelatedExportItemValues, PickListExportItemNameMap, getAllPickListExportFields } from "../../mapping";

interface ExportItemCheckboxGroupProps {
    value?: PickListExportItem[];
    totalPickListExportItems?: PickListExportItem[];
    editable?: boolean;
    onChange?: (value: PickListExportItem[]) => void;
    onFieldOrderChange?: (value: PickListExportItem[]) => void;
}

interface DraggableExportItemProps {
    field: PickListExportItem;
    index: number;
    onDrag: (from: number, to: number) => void;
    onDragEnd: () => void;
}

const renderExportItemName = (field: PickListExportItem) => {
    const name = PickListExportItemNameMap[field];

    if (field === PickListExportItem.REFUNDING_QUANTITY) {
        return (
            <PopoverText content="会从退款中订单中统计正在退款的单品数量，方便计算补货数量">
                {name}
            </PopoverText>
        );
    }

    return name;
};

const DraggableExportItem: React.FC<DraggableExportItemProps> = ({ field, index, onDrag, onDragEnd }) => {
    const { ref, opacity } = useDragDrop({
        index,
        onChange: onDrag,
        direction: "both",
        onChangeEnd: onDragEnd,
    });

    return (
        <span ref={ref} className="pick-list-export-field-item is-draggable" style={{ opacity }}>
            <Checkbox value={field}>{renderExportItemName(field)}</Checkbox>
        </span>
    );
};

const ExportItemCheckboxGroup: React.FC<ExportItemCheckboxGroupProps> = function (props) {
    const {
        value = [],
        totalPickListExportItems = getAllPickListExportFields(),
        editable = true,
        onChange,
        onFieldOrderChange,
    } = props;

    const sortableFields = useMemo(
        () => totalPickListExportItems.filter((field) => !orderRelatedExportItemValues.includes(field)),
        [totalPickListExportItems],
    );
    const orderRelatedFields = useMemo(
        () => totalPickListExportItems.filter((field) => orderRelatedExportItemValues.includes(field)),
        [totalPickListExportItems],
    );

    const { runAsync: saveSetting, loading } = useRequest(
        async (pickListExportFieldsStatus: PickListExportFieldsStatus[]) => {
            await savePickListExportFieldsStatus(pickListExportFieldsStatus);

            message.success("已保存导出项设置");
        },
        {
            manual: true,
            debounceWait: 750,
        },
    );

    /**
     * 将选中的导出项值按照当前的导出项顺序重新排序
     */
    const sortSelectedFields = (
        allPickListExportItems: PickListExportItem[],
        selectedFields: PickListExportItem[] = [],
    ) => {
        const selectedFieldSet = new Set(selectedFields);

        return allPickListExportItems.filter((field) => selectedFieldSet.has(field));
    };

    const handleChange = (fields: PickListExportItem[]) => {
        if (!editable) return;

        const sortedFieldsValue = sortSelectedFields(totalPickListExportItems, fields);
        onChange?.(sortedFieldsValue);

        const needSaveFieldsStatus: PickListExportFieldsStatus[] = totalPickListExportItems.map((field) => {
            return {
                exportItem: field,
                checked: !!sortedFieldsValue?.includes(field),
            };
        });

        saveSetting(needSaveFieldsStatus);
    };

    const handleDragEnd = (newSortableFields: PickListExportItem[]) => {
        const newFields = [...newSortableFields, ...orderRelatedExportItemValues];
        const sortedFieldsValue = sortSelectedFields(newFields, value);

        onFieldOrderChange?.(newFields);
        onChange?.(sortedFieldsValue);

        const needSaveFieldsStatus: PickListExportFieldsStatus[] = newFields.map((field) => {
            return {
                exportItem: field,
                checked: !!sortedFieldsValue?.includes(field),
            };
        });

        saveSetting(needSaveFieldsStatus);
    };

    const { localList, onLocalChange, onLocalDragEnd } = useDragDropDataTransformer(sortableFields, handleDragEnd);

    const renderCheckbox = (field: PickListExportItem) => {
        return (
            <span className="pick-list-export-field-item" key={field}>
                <Checkbox value={field}>{renderExportItemName(field)}</Checkbox>
            </span>
        );
    };

    return (
        <Spin spinning={loading}>
            <Checkbox.Group
                className="exportFields-label-container"
                value={value}
                onChange={(fields) => handleChange(fields as PickListExportItem[])}
            >
                <Tooltip title={editable ? "支持拖拽排序" : null} placement="right">
                    <div className="pick-list-sortable-export-fields">
                        {editable ? (
                            <DndProvider backend={HTML5Backend}>
                                {localList.map((field, index) => {
                                    return (
                                        <DraggableExportItem
                                            key={field}
                                            field={field}
                                            index={index}
                                            onDrag={onLocalChange}
                                            onDragEnd={onLocalDragEnd}
                                        />
                                    );
                                })}
                            </DndProvider>
                        ) : (
                            sortableFields.map(renderCheckbox)
                        )}
                    </div>
                </Tooltip>

                {orderRelatedFields.length ? (
                    <div className="pick-list-order-export-fields">{orderRelatedFields.map(renderCheckbox)}</div>
                ) : null}
            </Checkbox.Group>
        </Spin>
    );
};

export default observer(ExportItemCheckboxGroup);
